import { useMemo, useState } from 'react'
import { Box, Stack, Typography, useTheme, type Theme } from '@mui/material'
import { Layer, Rectangle, ResponsiveContainer, Sankey, Tooltip } from 'recharts'

import { chartTooltipStyle } from '../chartStyle'

/**
 * ── THE SHARED FLOW CHART ────────────────────────────────────────────────────────────────
 *
 * VRG-691 built this for GTM → Sales → Pipeline (lead sources → stages → drop-offs), and it is
 * deliberately generic so VRG-690's site-flow Sankey (entry → next → exit) can render through
 * it rather than a second copy (§36). It takes nodes and links by STRING id and does the index
 * bookkeeping recharts wants; the caller never sees an index.
 *
 * House style, from `chartStyle`: the brand (purple) ramp for nodes, links in the source
 * node's colour at low opacity, the paper tooltip. A node's colour comes from its `tone`:
 *   `source`  a ramp step per source (so neighbouring sources are distinguishable)
 *   `stage`   brand[600] — the main flow
 *   `end`     grey — drop-offs read as recessive next to the flow
 *   `success` brand[800] — the terminal everyone is trying to reach
 *
 * Hover shows the count (or the caller's formatted value) and its share: a node's share of
 * everything entering the chart, a link's share of what left its source node. A node can be
 * clickable (`onNodeClick`); the selected one is outlined and the rest dim.
 *
 * VRG-696 (the master pipeline) added three optional, backward-compatible inputs, so a chart can
 * show the count BEHIND a revenue-weighted band and say when that count is small:
 *   `n`         the number of records in a node or band, shown in the tooltip (and, with
 *               `caption`, on the node label) whatever the band is weighted by;
 *   `detail`    extra tooltip lines, e.g. a band's actual vs predicted split;
 *   `smallSampleBelow` a node or band with `n` under it renders its percentage greyed and says
 *               "Small sample" — de-emphasised, NEVER hidden.
 *
 * Animation is off for the reason `CHART_BAR_PROPS` records: recharts draws nothing until
 * `requestAnimationFrame` ticks, so a throttled tab or the preview pane renders an empty chart.
 */
export type SankeyTone = 'source' | 'stage' | 'end' | 'success'

export interface SankeyChartNode {
  id: string
  label: string
  tone?: SankeyTone
  /** Hover-only line under the figure, e.g. "click to filter the board". */
  hint?: string
  clickable?: boolean
  /** VRG-696 — records behind this node (shown even when the flow is weighted by money). */
  n?: number
  /** VRG-696 — appended to the node's on-chart label after the figure, e.g. "n 12 · 40%". */
  caption?: string
  /** VRG-696 — extra tooltip lines. */
  detail?: readonly string[]
}

export interface SankeyChartLink {
  source: string
  target: string
  value: number
  /** VRG-696 — records behind this band. */
  n?: number
  /** VRG-696 — extra tooltip lines. */
  detail?: readonly string[]
}

export interface SankeyChartProps {
  nodes: readonly SankeyChartNode[]
  links: readonly SankeyChartLink[]
  height?: number
  /** Formats a flow figure for labels and the tooltip. Defaults to a plain count. */
  formatValue?: (v: number) => string
  /** The noun for the tooltip ("deals"); omitted when the figure carries its own unit. */
  unit?: string
  onNodeClick?: (node: SankeyChartNode) => void
  selectedNodeId?: string | null
  ariaLabel: string
  /** VRG-696 — the noun for `n` in the tooltip ("leads"). */
  nUnit?: string
  /** VRG-696 — a node/band whose `n` is below this greys its percentage and says "Small sample". */
  smallSampleBelow?: number
  /** Room right of the last column for its labels (px). Longer labels with a `caption` want more. */
  labelGutter?: number
  /**
   * VRG-696 — draw each column's nodes in the order GIVEN (recharts' `sort={false}`), instead of
   * letting the layout reorder them by weight. The pipeline charts pass it so "Not recorded"
   * (and "Website, referrer unknown") always sit at the bottom of the source column, where a
   * reader looks for the leftovers, whatever their size. Off by default: the website flows
   * chart has no such order to keep.
   */
  preserveOrder?: boolean
}

const SOURCE_RAMP = [700, 500, 300, 800, 400, 600, 900, 200] as const

const toneColor = (theme: Theme, tone: SankeyTone | undefined, sourceIndex: number): string => {
  const brand = theme.palette.brand as unknown as Record<number, string>
  if (tone === 'source') return brand[SOURCE_RAMP[sourceIndex % SOURCE_RAMP.length]]
  if (tone === 'end') return theme.palette.grey[400]
  if (tone === 'success') return brand[800]
  return brand[600]
}

interface Payload {
  name: string
  value: number
  id: string
  color: string
  node: SankeyChartNode
}

const defaultFormat = (v: number) => v.toLocaleString('en-US')

export const SankeyChart = ({
  nodes,
  links,
  height = 360,
  formatValue = defaultFormat,
  unit,
  onNodeClick,
  selectedNodeId = null,
  ariaLabel,
  nUnit,
  smallSampleBelow,
  labelGutter = 168,
  preserveOrder = false,
}: SankeyChartProps) => {
  const theme = useTheme()
  const [hovered, setHovered] = useState<string | null>(null)

  const data = useMemo(() => {
    // Only nodes that carry flow: recharts lays out every node it is given, and a node with no
    // links would sit at the left edge as a stray bar.
    const live = links.filter((l) => l.value > 0)
    const used = new Set(live.flatMap((l) => [l.source, l.target]))
    const kept = nodes.filter((n) => used.has(n.id))
    const index = new Map(kept.map((n, i) => [n.id, i]))
    let s = 0
    const colored = kept.map((n) => {
      const color = toneColor(theme, n.tone, n.tone === 'source' ? s++ : 0)
      return { name: n.label, id: n.id, color, node: n }
    })
    return {
      nodes: colored,
      links: live
        .filter((l) => index.has(l.source) && index.has(l.target))
        .map((l) => ({ source: index.get(l.source)!, target: index.get(l.target)!, value: l.value })),
    }
  }, [nodes, links, theme])

  // Everything entering the chart: the outflow of nodes nothing flows into.
  const total = useMemo(() => {
    const targets = new Set(data.links.map((l) => l.target))
    return data.links.filter((l) => !targets.has(l.source)).reduce((t, l) => t + l.value, 0)
  }, [data])
  const outOf = useMemo(() => {
    const m = new Map<number, number>()
    for (const l of data.links) m.set(l.source, (m.get(l.source) ?? 0) + l.value)
    return m
  }, [data])

  const pct = (v: number, of: number) => (of > 0 ? `${Math.round((v / of) * 100)}%` : '—')
  const linkById = useMemo(() => new Map(links.map((l) => [`${l.source}>${l.target}`, l])), [links])
  const isSmall = (n: number | undefined) => smallSampleBelow !== undefined && n !== undefined && n < smallSampleBelow
  const dimmed = (id: string) => selectedNodeId !== null && selectedNodeId !== id

  if (data.links.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body3" color="text.secondary">
          Nothing flows in this range.
        </Typography>
      </Box>
    )
  }

  const tooltip = chartTooltipStyle(theme)

  return (
    <Box role="img" aria-label={ariaLabel} sx={{ width: '100%', height }} data-testid="sankey-chart">
      <ResponsiveContainer width="100%" height="100%">
        <Sankey
          data={data}
          nodePadding={18}
          nodeWidth={12}
          iterations={64}
          sort={!preserveOrder}
          margin={{ top: 8, right: labelGutter, bottom: 8, left: 8 }}
          // @ts-expect-error recharts' Sankey types omit isAnimationActive, which it honours.
          isAnimationActive={false}
          link={(props: {
            sourceX: number
            targetX: number
            sourceY: number
            targetY: number
            sourceControlX: number
            targetControlX: number
            linkWidth: number
            index: number
            payload: { source: Payload; target: Payload; value: number }
          }) => {
            const { sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload } = props
            const src = payload.source
            const faded = dimmed(src.id) && dimmed(payload.target.id)
            const lit = hovered === src.id || hovered === payload.target.id
            return (
              <path
                d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
                fill="none"
                stroke={src.color}
                strokeWidth={Math.max(1, linkWidth)}
                strokeOpacity={faded ? 0.08 : lit ? 0.5 : 0.28}
              />
            )
          }}
          node={(props: { x: number; y: number; width: number; height: number; index: number; payload: Payload & { value: number } }) => {
            const { x, y, width, height: h, payload } = props
            const n = payload.node
            const clickable = !!onNodeClick && n.clickable !== false && n.tone === 'source'
            const selected = selectedNodeId === n.id
            const small = isSmall(n.n)
            return (
              <Layer
                key={`node-${n.id}`}
                onMouseEnter={() => setHovered(n.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={clickable ? () => onNodeClick?.(n) : undefined}
                style={{ cursor: clickable ? 'pointer' : 'default' }}
                data-testid={`sankey-node-${n.id}`}
              >
                <Rectangle
                  x={x}
                  y={y}
                  width={width}
                  height={Math.max(2, h)}
                  fill={payload.color}
                  fillOpacity={dimmed(n.id) ? 0.35 : 1}
                  stroke={selected ? theme.palette.text.primary : 'none'}
                  strokeWidth={selected ? 2 : 0}
                  radius={3}
                />
                <text
                  x={x + width + 8}
                  y={y + h / 2}
                  dy="0.35em"
                  fontSize={12}
                  fill={dimmed(n.id) ? theme.palette.text.disabled : theme.palette.text.primary}
                >
                  {payload.name}
                  <tspan fill={small ? theme.palette.text.disabled : theme.palette.text.secondary}>{`  ${formatValue(payload.value)}`}</tspan>
                  {n.caption && (
                    <tspan fill={small ? theme.palette.text.disabled : theme.palette.text.secondary} data-small-sample={small ? 'true' : undefined}>
                      {`  ${n.caption}`}
                    </tspan>
                  )}
                </text>
              </Layer>
            )
          }}
        >
          <Tooltip
            {...tooltip}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null
              const item = payload[0] as unknown as { payload: { payload?: unknown } & Record<string, unknown> }
              const p = (item.payload?.payload ?? item.payload) as Record<string, unknown>
              let title: string
              let line: string
              let hint: string | undefined
              let share: string
              let n: number | undefined
              let detail: readonly string[] | undefined
              if (p && 'source' in p && 'target' in p) {
                const src = p.source as Payload
                const tgt = p.target as Payload
                const v = Number(p.value) || 0
                const srcIdx = data.nodes.findIndex((n) => n.id === src.id)
                title = `${src.name} → ${tgt.name}`
                line = `${formatValue(v)}${unit ? ` ${unit}` : ''}`
                share = `${pct(v, outOf.get(srcIdx) ?? 0)} of ${src.name}`
                const orig = linkById.get(`${src.id}>${tgt.id}`)
                n = orig?.n
                detail = orig?.detail
              } else {
                const node = p as unknown as Payload & { value: number }
                title = node.name
                line = `${formatValue(node.value)}${unit ? ` ${unit}` : ''}`
                share = `${pct(node.value, total)} of all`
                hint = node.node?.hint
                n = node.node?.n
                detail = node.node?.detail
              }
              const small = isSmall(n)
              return (
                <Stack spacing={1} sx={{ ...tooltip.contentStyle, p: 3, maxWidth: 280 }}>
                  <Typography variant="body3" sx={{ color: 'text.secondary' }}>
                    {title}
                  </Typography>
                  <Typography variant="body3" sx={{ color: 'text.primary' }}>
                    {line}
                    <Box component="span" sx={{ color: small ? 'text.disabled' : 'inherit' }}>{` · ${share}`}</Box>
                  </Typography>
                  {n !== undefined && (
                    <Typography variant="body3" sx={{ color: 'text.secondary' }}>
                      {`n = ${n.toLocaleString('en-US')}${nUnit ? ` ${n === 1 ? nUnit.replace(/s$/, '') : nUnit}` : ''}`}
                      {small ? ' · small sample, read the share with care' : ''}
                    </Typography>
                  )}
                  {detail?.map((d) => (
                    <Typography key={d} variant="body3" sx={{ color: 'text.secondary' }}>
                      {d}
                    </Typography>
                  ))}
                  {hint && (
                    <Typography variant="body3" sx={{ color: 'text.secondary' }}>
                      {hint}
                    </Typography>
                  )}
                </Stack>
              )
            }}
          />
        </Sankey>
      </ResponsiveContainer>
    </Box>
  )
}
